import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { useSTKContext } from '@/context/context';
import {
  FranchiseeBundleAddOnRow,
  FranchiseeBundleAddOnsResponse,
  FranchiseeBundleAddOnStoreGroupPrice,
  NamedPrice,
  NamedPriceByStoreDto,
  NamedPriceDetail,
  NamedPriceModifierSelection,
  NamedPricePriceDeviationsRequest,
  NamedPricePriceDeviationsResponse,
  NamedPriceStoreOverrideRequest,
} from '@/types/namedPrice.types';
import { PriceUpdateAPIResponse } from '@/types/stores.types';
import { StoreAndGroupType } from '@/context/context.types';

export const useNamedPrices = (enabled: boolean) => {
  const { apiClient } = useSTKContext();
  return useQuery<AxiosResponse<NamedPrice[]>>({
    queryKey: ['named-prices'],
    queryFn: () => apiClient.get<NamedPrice[]>('/named-prices'),
    enabled,
  });
};

export const useUpdateNamedPrice = (namedPriceId: string) => {
  const { apiClient } = useSTKContext();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<NamedPriceDetail>) => apiClient.put<NamedPrice>(`/named-prices/${namedPriceId}`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['named-prices'] });
      queryClient.invalidateQueries({ queryKey: ['named-prices', namedPriceId] });
    },
  });
};

export const useUpdateNamedPriceModifierFlags = () => {
  const { apiClient } = useSTKContext();
  return useMutation({
    mutationFn: (requestBody: NamedPriceModifierSelection[]) => apiClient.put('/named-prices/update', requestBody),
  });
};

export const useNamedPriceStoreOverride = () => {
  const { apiClient } = useSTKContext();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: NamedPriceStoreOverrideRequest) => {
      const response = await apiClient.put<PriceUpdateAPIResponse>('/named-prices/store-override', body);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['franchisee-bundle-add-ons'] });
      queryClient.invalidateQueries({ queryKey: ['named-prices'] });
    },
  });
};

function storeNumbersFromSelection(storesAndGroups: StoreAndGroupType | undefined): string[] {
  if (!storesAndGroups) return [];
  if (storesAndGroups.origin === 'tab-store' && storesAndGroups.stores?.length) {
    return storesAndGroups.stores.map((s) => s.storeNo);
  }
  if (storesAndGroups.origin === 'tab-stores-groups' && storesAndGroups.storeGroups?.length) {
    return storesAndGroups.storeGroups.flatMap((g) => g.stores ?? []);
  }
  return [];
}

function buildStoreGroupPriceFromRange(range: string): FranchiseeBundleAddOnStoreGroupPrice {
  const trimmed = range?.trim() ?? '';
  /* Backend returns "min-max" (no spaces); treat as range when dash present */
  const isRange = trimmed.includes('-');
  const priceDisplay = isRange
    ? trimmed
        .split('-')
        .map((s) => (s.trim().startsWith('$') ? s.trim() : s.trim() ? `$${s.trim()}` : ''))
        .filter(Boolean)
        .join(' - ') || (trimmed.startsWith('$') ? trimmed : `$${trimmed}`)
    : trimmed.startsWith('$')
    ? trimmed
    : trimmed
    ? `$${trimmed}`
    : '';
  return {
    priceDisplay,
    isRange,
    chosenPriceRange: trimmed,
  };
}

export function mapByStoresResponseToAddOns(dtos: NamedPriceByStoreDto[]): FranchiseeBundleAddOnRow[] {
  return dtos.map((dto) => {
    const storeGroupPrices: Record<string, FranchiseeBundleAddOnStoreGroupPrice> = {};
    for (const sg of dto.storeGroups ?? []) {
      storeGroupPrices[sg.storeGroupName] = buildStoreGroupPriceFromRange(sg.chosenPriceRange ?? '');
    }
    return {
      namedPriceId: dto.namedPriceId,
      name: dto.name ?? '',
      isLock: dto.priceLock ?? false,
      storeGroupPrices,
      availablePrices: dto.availablePrices,
      effectedBundles: dto.effectedBundles,
    };
  });
}

export const useFranchiseeBundleAddOns = (enabled: boolean, storesAndGroups: StoreAndGroupType | undefined) => {
  const { apiClient } = useSTKContext();
  const storeNumbers = storeNumbersFromSelection(storesAndGroups);
  const hasSelection = storeNumbers.length > 0;

  return useQuery<FranchiseeBundleAddOnsResponse>({
    queryKey: ['franchisee-bundle-add-ons', storeNumbers],
    queryFn: async () => {
      try {
        const response = await apiClient.post<NamedPriceByStoreDto[]>('/named-prices/by-stores', storeNumbers);
        const dtos = response.data ?? [];
        return { addOns: mapByStoresResponseToAddOns(dtos) };
      } catch {
        return { addOns: [] };
      }
    },
    enabled: enabled && hasSelection,
  });
};

export const useNamedPricePriceDeviations = (enabled: boolean, namedPriceId: string, stores: string[]) => {
  const { apiClient } = useSTKContext();
  const reqBody: NamedPricePriceDeviationsRequest = { namedPriceId, stores };
  return useQuery({
    queryKey: ['named-price-price-deviations', { namedPriceId, stores }],
    queryFn: async () => {
      const response = await apiClient.put<NamedPricePriceDeviationsResponse>(
        '/named-prices/price-deviations',
        reqBody,
      );
      return response.data;
    },
    enabled: enabled && !!namedPriceId && stores.length > 0,
  });
};
